import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, ChevronDown } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { formatNumber } from '../utils/format';

// Article shape as the legal-acts API returns it (src/services/legal_acts_service.py
// _article_dict): `label` is the article number ("Art. 15"), `rubric` its own
// heading, `section_title` the enclosing chapter.
export interface VirtualizedArticle {
  id: string | number;
  label: string | null;
  rubric?: string | null;
  section_title?: string | null;
  content: string;
}

interface VirtualizedArticleListProps {
  articles: VirtualizedArticle[];
  itemsPerPage?: number;
  renderHeader?: (article: VirtualizedArticle) => React.ReactNode;
  renderActions?: (article: VirtualizedArticle) => React.ReactNode;
}

export default function VirtualizedArticleList({
  articles,
  itemsPerPage = 20,
  renderHeader,
  renderActions
}: VirtualizedArticleListProps) {
  const { t } = useTranslation();
  const [displayedArticles, setDisplayedArticles] = useState<VirtualizedArticle[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedArticles, setExpandedArticles] = useState<Set<string | number>>(new Set());
  const [searchFilter, setSearchFilter] = useState('');

  // Filter articles based on search
  const filteredArticles = useMemo(() => {
    if (!searchFilter) return articles;

    const lowerSearch = searchFilter.toLowerCase();
    return articles.filter(article => {
      const searchableText = `${article.label || ''} ${article.section_title || ''} ${article.rubric || ''} ${article.content}`.toLowerCase();
      return searchableText.includes(lowerSearch);
    });
  }, [articles, searchFilter]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);

  // Update displayed articles when page changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setDisplayedArticles(filteredArticles.slice(startIndex, endIndex));
  }, [currentPage, filteredArticles, itemsPerPage]);

  // Reset to first page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchFilter]);

  const toggleArticleContent = useCallback((articleId: string | number) => {
    setExpandedArticles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(articleId)) {
        newSet.delete(articleId);
      } else {
        newSet.add(articleId);
      }
      return newSet;
    });
  }, []);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of article list
      const listElement = document.getElementById('article-list-top');
      if (listElement) {
        listElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  if (articles.length === 0) {
    return (
      <p className="text-gray-500">{t('articleList.noResults')}</p>
    );
  }

  const from = formatNumber(((currentPage - 1) * itemsPerPage) + 1);
  const to = formatNumber(Math.min(currentPage * itemsPerPage, filteredArticles.length));
  const total = formatNumber(filteredArticles.length);

  return (
    <div className="space-y-4">
      {/* Search and info bar */}
      <div id="article-list-top" className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-600">
          {t('articleList.showing', { from, to, total })}
          {searchFilter && ` ${t('articleList.filteredFrom', { total: formatNumber(articles.length) })}`}
        </div>
        <input
          type="text"
          placeholder={t('articleList.searchPlaceholder')}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          className="px-3 py-1 border rounded-md text-sm w-64"
        />
      </div>

      {/* Article list */}
      <div className="space-y-3">
        {displayedArticles.map((article) => (
          <div key={article.id} className="border rounded-md p-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                {/* Article header */}
                {renderHeader ? (
                  renderHeader(article)
                ) : (
                  <div>
                    <div className="font-medium">
                      {article.label}
                      {article.section_title && (
                        <span className="ml-2 text-gray-500">
                          {article.section_title}
                        </span>
                      )}
                    </div>
                    {article.rubric && (
                      <div className="text-sm text-gray-600 mt-1">
                        {article.rubric}
                      </div>
                    )}
                  </div>
                )}

                {/* Article content */}
                <div className="mt-2 text-sm">
                  <div className="relative">
                    <div className={`${!expandedArticles.has(article.id) ? "max-h-20 overflow-hidden" : ""}`}>
                      <ReactMarkdown>{article.content}</ReactMarkdown>
                    </div>

                    {/* Gradient fade effect when collapsed */}
                    {!expandedArticles.has(article.id) && (article.content?.length ?? 0) > 200 && (
                      <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent"></div>
                    )}
                  </div>

                  {(article.content?.length ?? 0) > 200 && (
                    <button
                      onClick={() => toggleArticleContent(article.id)}
                      className="text-primary-500 hover:text-primary-700 text-sm flex items-center mt-1"
                    >
                      {expandedArticles.has(article.id) ? (
                        <>
                          <ChevronUp size={16} className="mr-1" />
                          {t('articleList.showLess')}
                        </>
                      ) : (
                        <>
                          <ChevronDown size={16} className="mr-1" />
                          {t('articleList.showAll')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Actions */}
              {renderActions && (
                <div className="ml-4">
                  {renderActions(article)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t('common.previous')}
          </button>

          {/* Page numbers */}
          <div className="flex gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={i}
                  onClick={() => goToPage(pageNum)}
                  className={`px-3 py-1 border rounded-md ${
                    pageNum === currentPage
                      ? 'bg-primary-600 text-white'
                      : 'hover:bg-gray-50'
                  }`}
                >
                  {formatNumber(pageNum)}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t('common.next')}
          </button>

          <span className="ml-2 text-sm text-gray-600">
            {t('articleList.page', { current: formatNumber(currentPage), total: formatNumber(totalPages) })}
          </span>
        </div>
      )}
    </div>
  );
}
